import React from 'react';
import {
  fetchZones,
  fetchZonesPublic,
  GeoPlace,
  reverseGeocode,
  reverseGeocodePublic,
  searchAddresses,
  searchAddressesPublic,
  ZoneAtPoint,
  zoneAtPoint,
  zoneAtPointPublic,
} from '../../../../api/geo-client';

/**
 * WHICH ROUTES THE MAP ASKS — staff or signed out.
 *
 * The building location picker and the time-zone field are used in two places:
 * the building forms (signed-in staff, /geo/*) and, since 2026-10-14, the
 * signup wizard's first building (no session yet, /geo/public/*). The
 * components are the same; only who answers differs, so the answer is chosen
 * here once and read from context rather than threaded through every prop.
 *
 * The staff entries call the module's functions LATE (an arrow, not the
 * function itself), so a test that mocks '../../../../api/geo-client' still
 * intercepts every call.
 */
export interface GeoAccess {
  signedOut: boolean;
  search: (
    q: string,
    near?: { lat: number; lng: number } | null,
    onFailed?: () => void
  ) => Promise<GeoPlace[]>;
  reverse: (lat: number, lng: number) => Promise<GeoPlace | null>;
  zoneAt: (lat: number, lng: number) => Promise<ZoneAtPoint | null>;
  zones: () => Promise<string[]>;
}

export const STAFF_GEO: GeoAccess = {
  signedOut: false,
  search: (q, near, onFailed) => searchAddresses(q, near, onFailed),
  reverse: (lat, lng) => reverseGeocode(lat, lng),
  zoneAt: (lat, lng) => zoneAtPoint(lat, lng),
  zones: () => fetchZones(),
};

export const PUBLIC_GEO: GeoAccess = {
  signedOut: true,
  search: (q, near, onFailed) => searchAddressesPublic(q, near, onFailed),
  reverse: (lat, lng) => reverseGeocodePublic(lat, lng),
  zoneAt: (lat, lng) => zoneAtPointPublic(lat, lng),
  zones: () => fetchZonesPublic(),
};

export const GeoAccessContext = React.createContext<GeoAccess>(STAFF_GEO);
